import type React from "react";
import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { reportingPeriod } from "@duress/shared";
import {
  completionRateBand,
  completionRateColor,
  fmtInt,
  fmtPct,
} from "@/lib/format";
import { BRAND } from "@/lib/colors";
import { ChartTooltip, useChartTheme } from "@/lib/chart";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { BreakdownBars, type BreakdownBar } from "../dashboard/BreakdownBars";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";

type CheckinData = reportingPeriod.CheckinData;
type CheckinUserEntry = reportingPeriod.CheckinUserEntry;
type CheckinStoreEntry = reportingPeriod.CheckinStoreEntry;

type RankedUser = CheckinUserEntry & { rank: number };

// ── Cell renderers ────────────────────────────────────────────────────────────

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const optionalNumCell = (v: unknown): React.ReactNode =>
  v == null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
  );

const userCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const teamCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

const teamMutedCell = (v: unknown): React.ReactNode => (
  <span className="text-muted-foreground">{typeof v === "string" ? v : ""}</span>
);

const rankCell = (v: unknown): React.ReactNode => (
  <span className="num text-muted-foreground">
    #{fmtInt(typeof v === "number" ? v : Number(v))}
  </span>
);

const completionRateCell = (v: unknown): React.ReactNode => {
  if (v == null) {
    return (
      <span className="text-muted-foreground" data-completion-band="none">
        —
      </span>
    );
  }
  const rate = typeof v === "number" ? v : Number(v);
  if (isNaN(rate)) {
    return (
      <span className="text-muted-foreground" data-completion-band="none">
        —
      </span>
    );
  }
  const color = completionRateColor(rate);
  const band = completionRateBand(rate);
  return (
    <span
      className="num font-semibold"
      style={color ? { color } : undefined}
      data-completion-band={band ?? "none"}
    >
      {fmtPct(rate)}
    </span>
  );
};

// ── Columns ──────────────────────────────────────────────────────────────────

function rankMissed(users: CheckinUserEntry[]): RankedUser[] {
  return [...users]
    .sort((a, b) => b.missed - a.missed)
    .map((u, i) => ({ ...u, rank: i + 1 }));
}

const MISSED_COLUMNS: DataTableColumn<RankedUser>[] = [
  { key: "rank", label: "Rank", render: rankCell },
  { key: "user", label: "User", render: userCell },
  { key: "store", label: "Team", render: teamMutedCell },
  { key: "missed", label: "Missed", render: numCell },
  { key: "completion_rate", label: "Completion Rate", render: completionRateCell },
];

const TEAM_COLUMNS: DataTableColumn<CheckinStoreEntry>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "checkins", label: "Check-ins", render: numCell },
  { key: "completed", label: "Completed", render: optionalNumCell },
  { key: "missed", label: "Missed", render: optionalNumCell },
  { key: "completion_rate", label: "Completion Rate", render: completionRateCell },
];

// ── Tile helpers ──────────────────────────────────────────────────────────────

function buildTiles(checkin: CheckinData): KPITile[] {
  const completionColor = completionRateColor(checkin.overall_completion_rate);

  const completionTile: KPITile = {
    label: "Completion Rate",
    value: fmtPct(checkin.overall_completion_rate),
    ...(completionColor ? { color: completionColor } : {}),
    ...(checkin.overall_completion_rate == null
      ? { sub: "no Status column in source CSV" }
      : {}),
  };

  const avgTile: KPITile = {
    label: "Avg Duration",
    value:
      checkin.avg_duration_minutes == null
        ? "—"
        : `${fmtInt(checkin.avg_duration_minutes)}m`,
    ...(checkin.avg_duration_minutes == null
      ? { sub: "checkin/checkout times missing" }
      : {}),
  };

  return [
    { label: "Total Check-ins", value: fmtInt(checkin.total_checkins) },
    completionTile,
    {
      label: "Unique Users",
      value: fmtInt(checkin.by_user.length),
      sub: "users with a recognised name",
    },
    avgTile,
  ];
}

// ── Completion-by-team breakdown ─────────────────────────────────────────────

// Per-team completion rate, colour-coded by band (green ≥90 → red <50) and
// sorted worst-first so teams falling behind surface at the top for review.
function CompletionByTeam({ entries }: { entries: CheckinStoreEntry[] }): React.JSX.Element {
  const bars: BreakdownBar[] = entries
    .filter((e) => e.completion_rate != null)
    .sort((a, b) => (a.completion_rate ?? 0) - (b.completion_rate ?? 0))
    .map((entry) => {
      const rate = entry.completion_rate ?? 0;
      return {
        label: entry.store,
        display: fmtPct(rate),
        color: completionRateColor(rate) ?? BRAND.blue,
        pct: rate,
      };
    });

  return <BreakdownBars bars={bars} />;
}

// ── Pure presentational component ────────────────────────────────────────────

export interface CheckinActivityProps {
  data: reportingPeriod.DashboardData;
}

export function CheckinActivity({ data }: CheckinActivityProps): React.JSX.Element {
  const checkin = data.checkin;
  const chart = useChartTheme();
  const timelineTitleId = useId();

  if (checkin === null) {
    return (
      <div
        className="flex flex-col items-center justify-center py-20 text-center"
        data-checkin-state="not-uploaded"
      >
        <p className="text-4xl mb-4">📋</p>
        <p className="text-sm text-muted-foreground max-w-sm">
          This period has no check-in data uploaded. Re-upload the period with a check-in CSV to
          see this tab.
        </p>
      </div>
    );
  }

  if (checkin.total_checkins === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-20 text-center"
        data-checkin-state="empty-csv"
      >
        <p className="text-4xl mb-4">📭</p>
        <p className="text-sm text-muted-foreground max-w-sm">
          A check-in CSV was uploaded but contained 0 check-ins for this period.
        </p>
      </div>
    );
  }

  const tiles = buildTiles(checkin);
  const missedRows = rankMissed(checkin.missed_by_user);
  // Only overlay the "Completed" line when the period actually has completion
  // data — otherwise it renders as a flat zero line that reads as broken.
  const hasCompletionData = checkin.timeline.some((p) => p.completed > 0);
  // Per-team completion breakdown only renders when teams carry completion rates
  // (i.e. the source CSV had a Status column).
  const hasTeamCompletion = checkin.by_store.some((e) => e.completion_rate != null);

  return (
    <div className="space-y-6">
      <KPIGrid tiles={tiles} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px] items-start">
        {/* Left column: timeline + per-team completion stacked */}
        <div className="flex flex-col gap-[10px]">
        <div className="card-duress p-5">
          <p id={timelineTitleId} className="font-head font-semibold text-sm text-foreground mb-4">
            Check-ins Over Time
          </p>
          {checkin.timeline.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">
                No timeline data — check-in CSV is missing a date column.
              </p>
            </div>
          ) : (
            <div role="img" aria-labelledby={timelineTitleId}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart
                data={checkin.timeline}
                margin={{ left: 10, right: 20, top: 4, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="checkinArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.blue} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={BRAND.blue} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
                  stroke={chart.grid}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: chart.grid }} />
                <Legend wrapperStyle={{ fontFamily: "Inter", fontSize: 12, color: chart.axis }} />
                <Area
                  type="monotone"
                  dataKey="checkins"
                  name="Check-ins"
                  stroke={BRAND.blue}
                  strokeWidth={2}
                  fill="url(#checkinArea)"
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
                {hasCompletionData && (
                  <Line
                    type="monotone"
                    dataKey="completed"
                    name="Completed"
                    stroke={BRAND.green}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
            </div>
          )}
        </div>

        {hasTeamCompletion && (
          <div className="card-duress p-5" data-checkin-panel="completion-by-team">
            <p className="font-head font-semibold text-sm text-foreground mb-4">
              Completion by Team
            </p>
            <CompletionByTeam entries={checkin.by_store} />
          </div>
        )}
        </div>

        {/* Missed check-ins by user */}
        <div className="card-duress p-5">
          <p className="font-head font-semibold text-sm text-foreground mb-4">
            Missed Check-ins by User
          </p>
          {missedRows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">
                No missed check-ins this period 🎉
              </p>
            </div>
          ) : (
            <DataTable<RankedUser>
              columns={MISSED_COLUMNS}
              data={missedRows}
              defaultSort={{ key: "missed", dir: "desc" }}
              searchKeys={["user", "store"]}
            />
          )}
        </div>
      </div>

      {/* Team breakdown */}
      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Team Breakdown
        </p>
        <DataTable<CheckinStoreEntry>
          columns={TEAM_COLUMNS}
          data={checkin.by_store}
          defaultSort={{ key: "checkins", dir: "desc" }}
          searchKeys={["store"]}
        />
      </div>

      {/* Methodology note */}
      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          <strong>Completion</strong> is derived from the Status column in the source CSV — values
          like <em>Completed</em>, <em>Yes</em>, or <em>True</em> count as a completed check-in.
          When the source CSV has no Status column, completion-related cells render as “—”.{" "}
          <strong>Missed</strong> = check-ins − completed. The timeline groups events by check-in
          date. Users with blank names are bucketed under “Unknown” in totals but excluded from the
          per-user breakdown.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function CheckinActivityTab({
  clientId,
  view,
}: {
  clientId: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return <CheckinActivity data={data} />;
}
